/**
 * @param {HTMLElement} $element
 * @param {Object} options
 * @constructor
 */
export default function MouseSensor($element, options = {}) {

  const $document = angular.element(document);
  const _sloppyClickThreshold = 5;

  let _activeDragElement = null;
  let _firstClickPosition = null;
  let _dragStartEvent = null;

  $element.on('mousedown', _handleMouseDown);

  return {
    destroy
  };

  /**
   * @param {MouseEvent} event
   * @private
   */
  function _handleMouseDown(event) {

    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    if (!event.target.closest(`.${options.handleClass}`)) {
      return;
    }

    let target = event.target.closest(`.${options.itemClass}`);
    let margin = _getElementMargin(target);

    _activeDragElement = target;
    _firstClickPosition = _getMousePoint(event, target.getBoundingClientRect(), margin);

    $document
      .on('mousemove', _handleMouseMove)
      .one('mouseup', _handleMouseUp);

    event.preventDefault();
  }

  /**
   * @param {MouseEvent} event
   * @private
   */
  function _handleMouseMove(event) {

    const {target} = event;
    const point = _getMousePoint(event, _firstClickPosition.position);
    const x = (point.x - _firstClickPosition.x);
    const y = (point.y - _firstClickPosition.y);

    if (!_isClickThresholdExceeded(_firstClickPosition, point)) {
      return;
    }

    if (_dragStartEvent && _dragStartEvent.defaultPrevented) {
      _clear();
      return;
    }

    if (_dragStartEvent) {
      _dispatchEvent('move', {
        originalTarget: _activeDragElement,
        target,
        x,
        y
      }, false);
    } else {
      // Actual dragging starts now
      _dragStartEvent = _dispatchEvent('start', {
        target: _activeDragElement,
        ..._firstClickPosition
      });
    }

  }

  /**
   * @param {MouseEvent} event
   * @private
   */
  function _handleMouseUp({target, x, y}) {

    if (_dragStartEvent) {
      _dispatchEvent('finish', {
        originalTarget: _activeDragElement,
        target,
        x,
        y
      });
    }

    _clear();
  }

  /**
   * @param {MouseEvent} event
   * @param {ClientRect | DOMRect} position
   * @param {Object} margin
   * @returns {{x: Number, y: Number, position: ClientRect | DOMRect}}
   * @private
   */
  function _getMousePoint(event, position, margin = {}) {

    const offsetX = position.width - options.handleSize;
    const offsetY = position.height / 2;

    const x = event.clientX - position.left - offsetX;
    const y = event.clientY - position.top - offsetY;

    return {
      x,
      y,
      position,
      margin
    };
  }

  /**
   * @param {HTMLElement} target
   * @returns {Object}
   * @private
   */
  function _getElementMargin(target) {

    const {marginTop, marginRight, marginBottom, marginLeft} = getComputedStyle(target);

    return {
      top: parseInt(marginTop),
      right: parseInt(marginRight),
      bottom: parseInt(marginBottom),
      left: parseInt(marginLeft)
    };
  }

  /**
   * Unbind to destroy the functionality
   */
  function destroy() {
    $element.off('mousedown', _handleMouseDown);
  }

  /**
   * @param {String} name
   * @param {Object} detail
   * @param {Boolean} cancelable
   * @returns {CustomEvent}
   * @private
   */
  function _dispatchEvent(name, detail = {}, cancelable = true) {

    let customEvent = new CustomEvent(`draggable:${name}`, {
      cancelable,
      detail
    });

    $element[0].dispatchEvent(customEvent);

    return customEvent;
  }

  /**
   *
   * @param {ClientRect | DOMRect} original
   * @param {ClientRect | DOMRect} current
   * @returns {Boolean}
   * @private
   */
  function _isClickThresholdExceeded(original, current) {
    return Math.abs(current.x - original.x) >= _sloppyClickThreshold ||
      Math.abs(current.y - original.y) >= _sloppyClickThreshold;
  }

  /**
   * @private
   */
  function _clear() {
    $document.off('mousemove', _handleMouseMove);

    _activeDragElement = null;
    _firstClickPosition = null;
    _dragStartEvent = null;
  }
}
